import { useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { cn } from "@lib/utils";
import {
  useUploadSlots,
  useUploadQueueCollapsed,
  useUploadQueueAwaitingReselect,
  useUploadQueueDismissed,
  useUploadQueueAllSuccess,
  useUploadQueueStore,
  computeAggregate,
} from "~/stores/uploadQueueStore";
import {
  pauseUpload,
  resumeUpload,
  cancelUpload,
  removeUpload,
  retryUpload,
} from "~/lib/uploads";
import { ProgressRing } from "./ProgressRing";
import { UploadQueueRow } from "./UploadQueueRow";

// The corner queue. Rendered once from the Inertia layout so it persists
// across `router.visit`. Reads slot state from the module store; the Uppy
// singleton keeps that store live.
// After every slot reaches success, hold the all-green card up this long so the
// user sees the moment land, then auto-dismiss.
const AUTO_DISMISS_GRACE_MS = 4000;

export function UploadQueueWidget() {
  const slots = useUploadSlots();
  const collapsed = useUploadQueueCollapsed();
  const awaitingReselect = useUploadQueueAwaitingReselect();
  const dismissed = useUploadQueueDismissed();
  const allSuccess = useUploadQueueAllSuccess();
  const setCollapsed = useUploadQueueStore((s) => s.setCollapsed);
  const dismissQueue = useUploadQueueStore((s) => s.dismissQueue);

  const aggregate = useMemo(() => computeAggregate(slots), [slots]);

  // Auto-dismiss once — and only once — every slot is a success. Any error,
  // in-flight, or paused slot leaves `allSuccess` false, so a failure never
  // triggers this. The grace lets the all-green state register first.
  useEffect(() => {
    if (dismissed || !allSuccess) return;
    const handle = setTimeout(() => dismissQueue(), AUTO_DISMISS_GRACE_MS);
    return () => clearTimeout(handle);
  }, [dismissed, allSuccess, dismissQueue]);

  // Dismissed (gone from the corner), or nothing to show and nothing restored.
  if (dismissed) return null;
  if (slots.length === 0 && !awaitingReselect) return null;

  const summary =
    aggregate.activeCount > 0
      ? `Uploading ${aggregate.activeCount} · ${aggregate.aggregatePercent}%`
      : "All uploads complete";

  // Per-row X: an actual delete. The row goes, and any ingested Upload (a
  // completed transfer's video) is hard-deleted server-side — unlike the
  // card-level dismiss above, which only tidies the widget away.
  const handleDismiss = (id: string) => removeUpload(id);
  const handleRetry = (id: string) => retryUpload(id);

  return (
    <div
      data-testid="upload-queue-widget"
      className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] rounded-2xl border border-neutral-200 bg-neutral-white p-5 shadow-xl"
    >
      <div className="flex items-center gap-3">
        {aggregate.activeCount > 0 && (
          <ProgressRing percent={aggregate.aggregatePercent} size={20} strokeWidth={3} />
        )}
        <span className="text-base font-bold text-base-600">Uploads</span>
        <span className="text-sm text-neutral-500" data-testid="upload-queue-summary">
          {summary}
        </span>
        <button
          type="button"
          aria-label={collapsed ? "Expand uploads" : "Collapse uploads"}
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto flex size-7 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100"
        >
          {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </button>
        <button
          type="button"
          aria-label="Dismiss uploads"
          data-testid="upload-queue-dismiss"
          onClick={() => dismissQueue()}
          className="flex size-7 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100"
        >
          <X className="size-4" />
        </button>
      </div>

      {!collapsed && (
        <>
          {slots.length > 0 && (
            <div className="mt-4 flex flex-col gap-4">
              {slots.map((slot) => (
                <UploadQueueRow
                  key={slot.id}
                  slot={slot}
                  onPause={pauseUpload}
                  onResume={resumeUpload}
                  onCancel={cancelUpload}
                  onDismiss={handleDismiss}
                  onRetry={handleRetry}
                />
              ))}
            </div>
          )}

          {awaitingReselect && slots.length === 0 && (
            <p className="mt-3 text-xs text-neutral-500" data-testid="upload-queue-restored">
              An upload was interrupted. Re-select the same file to resume where it left off.
            </p>
          )}

          {aggregate.anyProcessing && (
            <p
              className={cn("mt-4 border-t border-neutral-100 pt-3 text-xs text-neutral-500")}
              data-testid="upload-queue-footer"
            >
              You can close this tab — we'll email you when your videos are ready.
            </p>
          )}
        </>
      )}
    </div>
  );
}
